/**
 * Silk Clinical Aesthetics,imagery manifest
 *
 * Canonical record of every image referenced by the site, plus aspirational
 * entries for assets called out in the photo shoot brief that do not yet
 * exist in /public.
 *
 * Why this file exists
 * --------------------
 * July 2026: real, descriptively-named treatment, device and clinic
 * photography (silk-{category}-{descriptor}-{nn}) replaced the thirteen
 * unlabelled `0J3A####-Enhanced-NR.jpg` source frames across treatment
 * cards, blog previews, contact and about pages. Staff headshots and the
 * homepage hero were intentionally left on their existing assets. A few
 * clinic-interior slots (reception, waiting area) still await a dedicated
 * shot and remain flagged `placeholder` below.
 *
 * This manifest is the source-of-truth for that migration:
 *   - `currentPath` is what is in /public today
 *   - `targetPath` is the descriptive path post-rename
 *   - `status: 'placeholder'` flags every image that needs a real shoot
 *   - `usage` tracks every page or component that consumes the image, so
 *     the rename can be done in one pass without missing references
 *
 * Physical renaming of the files in /public is handled separately. This
 * file is the data contract that the rename script and the photo brief
 * both read from.
 */

export type SiteImage = {
  /** Stable id used to reference an image from components. */
  id: string;
  /** Descriptive target filename: silk-{section}-{descriptor}-{nn}.{ext}. */
  filename: string;
  /** Current path in /public, or '' if the asset does not yet exist. */
  currentPath: string;
  /** Intended final path post-rename. */
  targetPath: string;
  /** Written, treatment-specific alt text. */
  alt: string;
  /** 'placeholder' = needs photo shoot or design asset. 'final' = ready. */
  status: "final" | "placeholder";
  /** Pages or components where this image renders. */
  usage: string[];
  /** Matching treatment slug if the image is treatment-specific. */
  treatment?: string;
  /** Coarse category, used by the photo brief and by component selectors. */
  category:
    | "hero"
    | "treatment"
    | "clinic"
    | "practitioner"
    | "before-after"
    | "device"
    | "brand";
  /** Clinical or framing detail the photo brief needs to capture. */
  notes?: string;
};

/**
 * Manifest entries fall into three groups:
 *
 *   1. Existing `0J3A####` source frames in /public/images/optimized/.
 *      One entry per physical file. Each is reused across multiple pages,
 *      which is exactly the problem this manifest is being built to fix.
 *
 *   2. Aspirational treatment, clinic, device and practitioner shots that
 *      the brief calls out but that do not yet exist. `currentPath: ''`
 *      flags these for the photo shoot.
 *
 *   3. Brand assets (logo, OG default) referenced from schema and metadata
 *      that need design work, not photography.
 */
export const IMAGERY: SiteImage[] = [
  // ---------------------------------------------------------------------
  // 1. Existing placeholder source frames (reused across the site)
  // ---------------------------------------------------------------------
  {
    id: "placeholder-3911",
    filename: "silk-treatment-volnewmer-detail-01.jpg",
    currentPath: "/images/optimized/silk-device-volnewmer-rf-machine-01.jpg",
    targetPath: "/images/optimized/silk-device-volnewmer-rf-machine-01.jpg",
    alt: "Volnewmer monopolar RF skin tightening device at Silk Clinical Aesthetics, Christchurch, the only Volnewmer clinic in the South Island",
    status: "final",
    usage: [
      "/",
      "/treatments",
      "/treatments/volnewmer-rf-skin-tightening",
      "/treatments/pdo-mono-threads",
      "/blog",
      "src/components/sections/TreatmentGrid.tsx",
      "src/components/sections/BlogPreview.tsx",
    ],
    treatment: "volnewmer-rf-skin-tightening",
    category: "treatment",
    notes:
      "Currently the most-overloaded asset on the site. Used as the homepage signature treatment hero, the Volnewmer treatment-page hero, the PDO threads page hero, and a blog preview. Real shoot must capture the Volnewmer handpiece and cooling tip clearly enough to read as the device, not generic clinic equipment.",
  },
  {
    id: "placeholder-3914",
    filename: "silk-hero-treatment-room-01.jpg",
    currentPath: "/images/optimized/silk-treatment-microdermabrasion-in-treatment-01.jpg",
    targetPath: "/images/optimized/silk-treatment-microdermabrasion-in-treatment-01.jpg",
    alt: "Skin specialist performing a microdermabrasion treatment in the Silk Clinical Christchurch room, soft natural light",
    status: "final",
    usage: [
      "/",
      "/treatments",
      "/treatments/ultraformer-mpt-hifu",
      "/treatments/lifting-threads",
      "/blog",
      "src/components/sections/Hero.tsx",
      "src/components/sections/TreatmentGrid.tsx",
      "src/components/sections/BlogPreview.tsx",
    ],
    category: "hero",
    notes:
      "Drives the homepage hero photo inset (priority LCP image). Also reused as Ultraformer HIFU and lifting-threads treatment hero. Brief should split this into a dedicated hero portrait-orientation shot and a separate Ultraformer device shot.",
  },
  {
    id: "placeholder-3916",
    filename: "silk-treatment-lip-filler-01.jpg",
    currentPath: "/images/optimized/silk-treatment-lip-filler-injection-01.jpg",
    targetPath: "/images/optimized/silk-treatment-lip-filler-injection-01.jpg",
    alt: "Lip filler treatment by a registered nurse at Silk Clinical Aesthetics, Christchurch, clinic-considered lighting",
    status: "final",
    usage: [
      "/treatments",
      "/treatments/lip-filler",
      "/treatments/prp-hair-loss",
      "/blog",
    ],
    treatment: "lip-filler",
    category: "treatment",
    notes:
      "Used for both lip filler and PRP for hair loss, which is a clinical mismatch. Needs two distinct real shots.",
  },
  {
    id: "placeholder-3925",
    filename: "silk-clinic-exterior-victoria-01.jpg",
    currentPath: "/images/optimized/silk-clinic-signage-exterior-01.jpg",
    targetPath: "/images/optimized/silk-clinic-signage-exterior-01.jpg",
    alt: "Silk Clinical Aesthetics exterior signage, 66 Victoria Street, Christchurch central",
    status: "final",
    usage: ["/", "/blog", "src/components/sections/LocationMap.tsx"],
    category: "clinic",
    notes:
      "Drives the Visit Us / LocationMap section. Real shoot should capture the 66 Victoria Street frontage in soft daylight, signage legible, no passers-by in frame.",
  },
  {
    id: "placeholder-3927",
    filename: "silk-treatment-microdermabrasion-01.jpg",
    currentPath: "/images/optimized/silk-treatment-microdermabrasion-closeup-01.jpg",
    targetPath: "/images/optimized/silk-treatment-microdermabrasion-closeup-01.jpg",
    alt: "Close detail of a microdermabrasion treatment at Silk Clinical Aesthetics, Christchurch",
    status: "final",
    usage: [
      "/treatments",
      "/treatments/microdermabrasion-sonophoresis",
      "/blog",
      "src/components/sections/BlogPreview.tsx",
    ],
    treatment: "microdermabrasion-sonophoresis",
    category: "treatment",
    notes:
      "Should clearly show the microdermabrasion handpiece and sonophoresis applicator together so the page image matches the treatment name.",
  },
  {
    id: "placeholder-3928",
    filename: "silk-clinic-waiting-area-01.jpg",
    currentPath: "/images/optimized/0J3A3928-Enhanced-NR.jpg",
    targetPath: "/images/clinic/silk-clinic-waiting-area-01.jpg",
    alt: "Silk Clinical Aesthetics waiting area, considered interior, soft tones",
    status: "placeholder",
    usage: ["/about", "/blog"],
    category: "clinic",
    notes:
      "About page Our Story photo. Should read as the actual clinic interior, not styled stock.",
  },
  {
    id: "placeholder-3929",
    filename: "silk-practitioner-shontelle-headshot-01.jpg",
    currentPath: "/images/optimized/0J3A3929-Enhanced-NR.jpg",
    targetPath: "/images/practitioner/silk-practitioner-shontelle-headshot-01.jpg",
    alt: "Shontelle Prasad, Founder and Lead Nurse Injector, Silk Clinical Aesthetics",
    status: "placeholder",
    usage: ["/about"],
    category: "practitioner",
    notes:
      "Currently a generic placeholder used as the team headshot for Shontelle Prasad. Needs a real professional portrait, 3:4 portrait orientation, neutral background consistent with clinic interior.",
  },
  {
    id: "placeholder-3930",
    filename: "silk-clinic-reception-01.jpg",
    currentPath: "/images/optimized/0J3A3930-Enhanced-NR.jpg",
    targetPath: "/images/clinic/silk-clinic-reception-01.jpg",
    alt: "Silk Clinical Aesthetics reception and lounge, considered interior detail",
    status: "placeholder",
    usage: ["/contact", "/blog"],
    category: "clinic",
    notes:
      "Contact page clinic photo. Should show the reception desk and lounge clearly so the visit cue is unambiguous.",
  },
  {
    id: "placeholder-3932",
    filename: "silk-treatment-combined-plan-01.jpg",
    currentPath: "/images/optimized/silk-treatment-anti-wrinkle-consultation-02.jpg",
    targetPath: "/images/optimized/silk-treatment-anti-wrinkle-consultation-02.jpg",
    alt: "Consultation for a combined treatment plan at Silk Clinical Aesthetics, Christchurch",
    status: "final",
    usage: ["/treatments", "/treatments/combined-treatment-plans"],
    treatment: "combined-treatment-plans",
    category: "treatment",
    notes:
      "Should suggest a multi-step plan visually (consultation chart, treatment list, the considered nature of a personalised plan) without showing identifiable patient details.",
  },
  {
    id: "placeholder-3935",
    filename: "silk-treatment-skin-peel-01.jpg",
    currentPath: "/images/optimized/silk-treatment-skin-cleansing-closeup-01.jpg",
    targetPath: "/images/optimized/silk-treatment-skin-cleansing-closeup-01.jpg",
    alt: "Close detail of skin cleansing and prep for a clinical skin peel at Silk Clinical Aesthetics, Christchurch",
    status: "final",
    usage: [
      "/treatments",
      "/treatments/skin-peels",
      "src/components/sections/TreatmentGrid.tsx",
    ],
    treatment: "skin-peels",
    category: "treatment",
    notes:
      "Used both for skin peels and for the homepage PDO threads card. Needs two distinct shots so the homepage card is not a peel image.",
  },
  {
    id: "placeholder-3936",
    filename: "silk-treatment-skin-hydration-01.jpg",
    currentPath: "/images/optimized/silk-treatment-anti-wrinkle-consultation-closeup-04.jpg",
    targetPath: "/images/optimized/silk-treatment-anti-wrinkle-consultation-closeup-04.jpg",
    alt: "Skin hydration injectable treatment prepared at Silk Clinical Aesthetics, Christchurch",
    status: "final",
    usage: ["/treatments", "/treatments/skin-hydration-injections", "/blog"],
    treatment: "skin-hydration-injections",
    category: "treatment",
    notes:
      "Should make the skin booster product visible enough that the page reads as a hydration treatment, not a generic injectable.",
  },
  {
    id: "placeholder-3960",
    filename: "silk-treatment-anti-wrinkle-01.jpg",
    currentPath: "/images/optimized/silk-treatment-anti-wrinkle-injection-01.jpg",
    targetPath: "/images/optimized/silk-treatment-anti-wrinkle-injection-01.jpg",
    alt: "Registered nurse preparing an anti-wrinkle injection at Silk Clinical Aesthetics, Christchurch, in clinical light",
    status: "final",
    usage: [
      "/treatments",
      "/treatments/anti-wrinkle-injections",
      "/treatments/prp-vampire-facial",
      "/blog",
      "src/components/sections/TreatmentGrid.tsx",
    ],
    treatment: "anti-wrinkle-injections",
    category: "treatment",
    notes:
      "Reused for both anti-wrinkle and PRP vampire facial. Needs separate shots: anti-wrinkle should suggest a 0.5mL syringe and prep, PRP should show the centrifuged plasma tube and microneedling pen.",
  },
  {
    id: "placeholder-3963",
    filename: "silk-treatment-dermal-filler-01.jpg",
    currentPath: "/images/optimized/silk-treatment-dermal-filler-injection-01.jpg",
    targetPath: "/images/optimized/silk-treatment-dermal-filler-injection-01.jpg",
    alt: "Dermal filler treatment by a registered nurse at Silk Clinical Aesthetics, Christchurch, considered detail",
    status: "final",
    usage: [
      "/treatments",
      "/treatments/dermal-fillers",
      "/treatments/dermal-fillers-for-men",
      "/treatments/fat-dissolver",
      "/blog",
      "src/components/sections/TreatmentGrid.tsx",
    ],
    treatment: "dermal-fillers",
    category: "treatment",
    notes:
      "Worst-offender for reuse: drives Dermal Fillers, Dermal Fillers for Men, Fat Dissolver, plus the homepage card. Needs three distinct treatment-specific shots so the male-focused page and the fat dissolver page do not share a visually identical hero with the standard fillers page.",
  },

  // ---------------------------------------------------------------------
  // 2. Aspirational shots called out by the brief but not yet shot
  // ---------------------------------------------------------------------
  {
    id: "ultraformer-device-01",
    filename: "silk-device-ultraformer-hifu-machine-01.jpg",
    currentPath: "/images/optimized/silk-device-ultraformer-hifu-machine-01.jpg",
    targetPath: "/images/optimized/silk-device-ultraformer-hifu-machine-01.jpg",
    alt: "Ultraformer MPT HIFU device and handpieces at Silk Clinical Aesthetics, Christchurch",
    status: "final",
    usage: ["/treatments/ultraformer-mpt-hifu"],
    treatment: "ultraformer-mpt-hifu",
    category: "device",
    notes:
      "Currently borrows the generic treatment-room frame. Needs a dedicated device-detail shot with the MPT cartridges visible (4.5mm, 3.0mm, 1.5mm) so the page reads as the latest-generation HIFU.",
  },
  {
    id: "volnewmer-device-01",
    filename: "silk-device-volnewmer-rf-authenticity-01.jpg",
    currentPath: "/images/optimized/silk-device-volnewmer-rf-authenticity-01.jpg",
    targetPath: "/images/optimized/silk-device-volnewmer-rf-authenticity-01.jpg",
    alt: "Volnewmer authenticity plaque at Silk Clinical Christchurch, confirming the only Volnewmer device in the South Island",
    status: "final",
    usage: ["/treatments/volnewmer-rf-skin-tightening", "/"],
    treatment: "volnewmer-rf-skin-tightening",
    category: "device",
    notes:
      "South Island exclusive credibility shot. Must show the Volnewmer wordmark on the device, confirming the claim. Distinct from the cooling-tip detail.",
  },
  {
    id: "pdo-thread-detail-01",
    filename: "silk-treatment-pdo-thread-01.jpg",
    currentPath: "",
    targetPath: "/images/treatments/silk-treatment-pdo-thread-01.jpg",
    alt: "PDO mono thread cannula and packaging laid out on a clean treatment tray",
    status: "placeholder",
    usage: ["/treatments/pdo-mono-threads"],
    treatment: "pdo-mono-threads",
    category: "treatment",
    notes:
      "PDO mono threads currently borrow the Volnewmer hero. Needs a specific thread-and-cannula detail.",
  },
  {
    id: "lifting-thread-detail-01",
    filename: "silk-treatment-lifting-thread-01.jpg",
    currentPath: "",
    targetPath: "/images/treatments/silk-treatment-lifting-thread-01.jpg",
    alt: "Barbed PDO lifting thread and applicator on a treatment tray, considered clinical detail",
    status: "placeholder",
    usage: ["/treatments/lifting-threads"],
    treatment: "lifting-threads",
    category: "treatment",
    notes:
      "Lifting threads page currently borrows the hero treatment-room shot. Needs a barbed-thread detail.",
  },
  {
    id: "prp-vampire-facial-01",
    filename: "silk-treatment-prp-vampire-01.jpg",
    currentPath: "",
    targetPath: "/images/treatments/silk-treatment-prp-vampire-01.jpg",
    alt: "Centrifuged platelet-rich plasma in a labelled tube beside a microneedling pen on a treatment tray",
    status: "placeholder",
    usage: ["/treatments/prp-vampire-facial"],
    treatment: "prp-vampire-facial",
    category: "treatment",
    notes:
      "Should make the PRP tube and microneedling pen both legible so the page is unambiguously about PRP plus microneedling.",
  },
  {
    id: "prp-hair-loss-01",
    filename: "silk-treatment-prp-hair-01.jpg",
    currentPath: "",
    targetPath: "/images/treatments/silk-treatment-prp-hair-01.jpg",
    alt: "Centrifuged platelet-rich plasma drawn into a fine syringe for scalp injection",
    status: "placeholder",
    usage: ["/treatments/prp-hair-loss"],
    treatment: "prp-hair-loss",
    category: "treatment",
    notes:
      "Distinct from the PRP vampire facial shot. Should suggest scalp injection prep, not facial.",
  },
  {
    id: "fat-dissolver-01",
    filename: "silk-treatment-fat-dissolver-01.jpg",
    currentPath: "",
    targetPath: "/images/treatments/silk-treatment-fat-dissolver-01.jpg",
    alt: "Fat dissolver injection prepped on a treatment tray, fine-gauge needle visible",
    status: "placeholder",
    usage: ["/treatments/fat-dissolver"],
    treatment: "fat-dissolver",
    category: "treatment",
    notes:
      "Currently borrows the dermal filler frame. Needs a distinct shot since the indication is fat reduction, not volume restoration.",
  },
  {
    id: "dermal-filler-men-01",
    filename: "silk-treatment-filler-men-01.jpg",
    currentPath: "",
    targetPath: "/images/treatments/silk-treatment-filler-men-01.jpg",
    alt: "Registered nurse marking jawline treatment points on a male client at Silk Clinical Christchurch",
    status: "placeholder",
    usage: ["/treatments/dermal-fillers-for-men"],
    treatment: "dermal-fillers-for-men",
    category: "treatment",
    notes:
      "Real consented male client preferred so the page does not read as women's-content with a different headline. If no consenting client available, a male-specific treatment-room detail is the next best option.",
  },
  {
    id: "before-after-volnewmer-01",
    filename: "silk-before-after-volnewmer-01.jpg",
    currentPath: "",
    targetPath: "/images/before-after/silk-before-after-volnewmer-01.jpg",
    alt: "Before and after Volnewmer RF skin tightening, real consented patient, six-month follow-up",
    status: "placeholder",
    usage: ["/treatments/volnewmer-rf-skin-tightening"],
    treatment: "volnewmer-rf-skin-tightening",
    category: "before-after",
    notes:
      "Requires written patient consent on file (template: /legal/consent-before-after.pdf). Per voice rules, no stock B&A imagery, ever. Same lighting, angle and expression in both frames. Six-month minimum follow-up to demonstrate collagen remodelling.",
  },
  {
    id: "before-after-anti-wrinkle-01",
    filename: "silk-before-after-anti-wrinkle-01.jpg",
    currentPath: "",
    targetPath: "/images/before-after/silk-before-after-anti-wrinkle-01.jpg",
    alt: "Before and after anti-wrinkle injections to the glabella, real consented patient, two-week follow-up",
    status: "placeholder",
    usage: ["/treatments/anti-wrinkle-injections"],
    treatment: "anti-wrinkle-injections",
    category: "before-after",
    notes:
      "Requires written patient consent on file. Two-week follow-up to capture full onset. Same expression in both frames or the comparison fails.",
  },
  {
    id: "before-after-dermal-filler-01",
    filename: "silk-before-after-dermal-filler-01.jpg",
    currentPath: "",
    targetPath: "/images/before-after/silk-before-after-dermal-filler-01.jpg",
    alt: "Before and after dermal filler to the cheeks and chin, real consented patient, two-week follow-up",
    status: "placeholder",
    usage: ["/treatments/dermal-fillers"],
    treatment: "dermal-fillers",
    category: "before-after",
    notes:
      "Requires written patient consent on file. Conservative result preferred over heroic transformation, consistent with the clinical-considered voice.",
  },

  // ---------------------------------------------------------------------
  // 3. Brand assets (design, not photography)
  // ---------------------------------------------------------------------
  {
    id: "silk-logo",
    filename: "silk-logo.png",
    currentPath: "",
    targetPath: "/images/logo/silk-logo.png",
    alt: "Silk Clinical Aesthetics",
    status: "placeholder",
    usage: ["src/lib/schema.ts", "src/lib/schema-builders.ts"],
    category: "brand",
    notes:
      "Referenced from MedicalBusiness schema and schema-builders. Required for organisation schema validation. PNG with transparent background, minimum 600x600 for schema use.",
  },
  {
    id: "og-default",
    filename: "og-default.jpg",
    currentPath: "",
    targetPath: "/images/og-default.jpg",
    alt: "Silk Clinical Aesthetics,Christchurch cosmetic injectables and clinical skin treatments",
    status: "placeholder",
    usage: ["src/lib/metadata.ts"],
    category: "brand",
    notes:
      "Default Open Graph share card used by every page that does not supply a custom OG image. 1200x630, brand-consistent typography over a clinic interior frame.",
  },
];

/** Look up an image by its stable id. */
export function getImageById(id: string): SiteImage | undefined {
  return IMAGERY.find((image) => image.id === id);
}

/**
 * Hero image path keyed by treatment slug. Single source of truth used by
 * both /treatments and /treatments/[slug]. Reuses the optimised placeholder
 * frames; will be replaced when the photo shoot lands.
 */
export const TREATMENT_HERO_IMAGES: Record<string, string> = {
  "silk-protocol": "/images/optimized/silk-clinic-signage-exterior-01.jpg",
  "volnewmer-rf-skin-tightening": "/images/optimized/silk-device-volnewmer-rf-machine-01.jpg",
  "ultraformer-mpt-hifu": "/images/optimized/silk-device-ultraformer-hifu-machine-01.jpg",
  "anti-wrinkle-injections": "/images/treatments/anti-wrinkle-injections.jpg",
  "dermal-fillers": "/images/optimized/silk-treatment-dermal-filler-injection-01.jpg",
  "lip-filler": "/images/treatments/lip-filler.jpg",
  "dermal-fillers-for-men": "/images/treatments/dermal-fillers-for-men.jpg",
  "skin-hydration-injections": "/images/optimized/silk-treatment-anti-wrinkle-consultation-closeup-04.jpg",
  "pdo-mono-threads": "/images/optimized/silk-treatment-microneedling-prep-01.jpg",
  "lifting-threads": "/images/treatments/lifting-threads.jpg",
  "prp-vampire-facial": "/images/treatments/prp-vampire-facial.jpg",
  "prp-hair-loss": "/images/treatments/prp-hair-loss.jpg",
  "skin-peels": "/images/optimized/silk-treatment-skin-cleansing-closeup-01.jpg",
  "microdermabrasion-sonophoresis": "/images/optimized/silk-treatment-microdermabrasion-closeup-01.jpg",
  microneedling: "/images/treatments/microneedling.jpg",
  "fat-dissolver": "/images/treatments/fat-dissolver.jpg",
  "combined-treatment-plans": "/images/optimized/silk-treatment-anti-wrinkle-consultation-01.jpg",
};

/** Return every image associated with a given treatment slug. */
export function getImagesByTreatment(slug: string): SiteImage[] {
  return IMAGERY.filter((image) => image.treatment === slug);
}

/** Return every image still flagged as a placeholder (needs a shoot). */
export function getPlaceholderImages(): SiteImage[] {
  return IMAGERY.filter((image) => image.status === "placeholder");
}

/**
 * Look up an image by its current /public path. Used by the rename pass
 * to migrate component image refs from `/images/optimized/0J3A####...`
 * to the descriptive `targetPath`.
 */
export function getImageByCurrentPath(path: string): SiteImage | undefined {
  return IMAGERY.find((image) => image.currentPath === path);
}
